import { FormEvent, useEffect, useMemo, useState } from "react";
import { UsersRound } from "lucide-react";
import Modal from "../ui/Modal";
import { createCompetitor } from "../../api/competitors";
import { OtaKey } from "../../types/ota";
import OtaUrlsFields, {
  buildOtaUrlsPayload,
  countFilledOtaUrls,
  EMPTY_OTA_VALUES,
  getInvalidOtaKeys,
  OtaValues,
} from "../ota/OtaUrlsFields";

type Props = {
  open: boolean;
  onClose: () => void;
  companyId?: number | null;
  hotelId?: number | null;
  otaLimit: number;
  onSubmitted?: () => void;
};

export default function AddCompetitorOtaModal({
  open,
  onClose,
  companyId,
  hotelId,
  otaLimit,
  onSubmitted,
}: Props) {
  const [name, setName] = useState("");
  const [urls, setUrls] = useState<OtaValues>(EMPTY_OTA_VALUES);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const limit = Math.max(1, Number.isFinite(otaLimit) ? otaLimit : 3);

  useEffect(() => {
    if (!open) {
      setName("");
      setUrls(EMPTY_OTA_VALUES);
      setLoading(false);
      setError("");
    }
  }, [open]);

  const filledCount = useMemo(() => countFilledOtaUrls(urls), [urls]);
  const invalidFields = useMemo(() => getInvalidOtaKeys(urls), [urls]);
  const hasInvalidUrls = invalidFields.size > 0;
  const isOverLimit = filledCount > limit;
  const saveDisabled = loading || !name.trim() || name.trim().length > 100 || hasInvalidUrls || isOverLimit;

  function handleUrlChange(key: OtaKey, value: string) {
    setUrls((current) => ({
      ...current,
      [key]: value,
    }));
    setError("");
  }

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    if (saveDisabled) return;

    if (!companyId || !hotelId) {
      setError("Не удалось определить объект размещения");
      return;
    }
    if (filledCount === 0) {
      setError("Добавьте хотя бы одну OTA-ссылку для конкурента");
      return;
    }

    const otaUrls = buildOtaUrlsPayload(urls);

    setLoading(true);
    setError("");
    try {
      await createCompetitor(companyId, hotelId, {
        name: name.trim(),
        url: otaUrls.official_website ?? Object.values(otaUrls)[0] ?? null,
        ota_urls: otaUrls,
      });
      onSubmitted?.();
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Не удалось добавить конкурента");
    } finally {
      setLoading(false);
    }
  }

  const baseInputClass =
    "w-full rounded-2xl border bg-white/80 px-4 py-[13px] text-[15px] text-slate-900 placeholder:text-slate-400 shadow-inner transition focus:ring-4 disabled:opacity-60 disabled:cursor-not-allowed";

  return (
    <Modal open={open} onClose={onClose} title="" widthClass="max-w-5xl">
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="text-center space-y-3 animate-softFade">
          <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-gradient-to-br from-indigo-100 to-purple-100 text-indigo-600 shadow-inner">
            <UsersRound size={24} />
          </div>
          <div>
            <h3 className="text-2xl font-semibold text-slate-900">Добавить конкурента</h3>
            <div className="mt-2 space-y-1 text-sm text-slate-500">
              <p>Укажите заголовок конкурента и ссылки на интересующие вас платформы.</p>
              <p>Согласно тарифу вы можете добавить до {limit} ссылок на каждого конкурента.</p>
            </div>
          </div>
        </div>

        <label className="block space-y-2">
          <span className="text-sm font-semibold text-slate-600">Название конкурента *</span>
          <input
            value={name}
            onChange={(event) => setName(event.target.value)}
            maxLength={100}
            className={`${baseInputClass} border-slate-200/80 focus:border-indigo-300 focus:ring-indigo-200/60`}
            placeholder="Например, Alpha Sirius"
            disabled={loading}
          />
          <span className="block text-right text-xs text-slate-400">{name.length}/100</span>
        </label>

        <OtaUrlsFields
          values={urls}
          onChange={handleUrlChange}
          disabled={loading}
          officialLabel="Сайт конкурента"
          title="Маппинг *"
        />

        {isOverLimit ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-700">
            На вашем тарифе доступно {limit} ссылок на ОТА для каждого конкурента, сейчас у вас указано {filledCount} ссылок. Вам необходимо уменьшить количество ссылок до {limit} что бы сохранить конкурента.
          </div>
        ) : null}

        {error ? (
          <div className="animate-softFade rounded-2xl border border-amber-200 bg-amber-50/90 px-4 py-3 text-sm text-amber-800 shadow-inner">
            {error}
          </div>
        ) : null}

        <div className="flex flex-col gap-3 pt-2 sm:flex-row sm:items-center sm:justify-center">
          <button
            type="submit"
            disabled={saveDisabled}
            className="inline-flex flex-1 items-center justify-center rounded-2xl bg-gradient-to-r from-indigo-600 to-purple-600 px-6 py-3 text-sm font-semibold text-white shadow-[0_18px_30px_rgba(99,102,241,0.35)] transition hover:translate-y-[-1px] hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50 sm:flex-none"
          >
            {loading ? "Сохраняем..." : "Сохранить"}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="inline-flex flex-1 items-center justify-center rounded-2xl border border-slate-200 bg-white px-6 py-3 text-sm font-semibold text-slate-600 transition hover:bg-slate-50 disabled:opacity-50 sm:flex-none"
          >
            Отмена
          </button>
        </div>
      </form>
    </Modal>
  );
}
